import {
    AdditionGroupFragment,
    AdditionGroupProductVariantFragment,
    CartItemFragment,
    ConfigurableProductFragment,
    ProductItemFragment,
    ProductVariantFragment,
    RemovableRecipeIngredientFragment,
    ToppingFragment,
    ToppingGroupFragment,
} from '@/features/graphql/generated/graphql'
import {
    Addon,
    AddonDefaults,
    AddonGroup,
    ConfigurableProduct,
    ProductVariant,
    ProductVariantDefaults,
    RemovableIngredient,
} from '@/features/pos/components/ProductConfigurationModal/types'
import { getProductVariantDefaults } from '@/features/pos/components/ProductConfigurationModal/utils/defaults'
import { formatFreeOfChargeAblePrice } from '@/utils/formatFreeOfChargeAblePrice'
import { TFunction } from 'i18next'

export const transformSelectedCategoryProductToConfigurable = (
    product: ProductItemFragment
): ConfigurableProduct => ({ name: product.name })

const formatAddonNameFromAdditionGroupProductVariant = (
    productVariant: AdditionGroupProductVariantFragment
): string => `${productVariant.product.name}${productVariant.name ? ` ${productVariant.name}` : ''}`

const transformAdditionGroupProductVariantToAddon = (
    productVariant: AdditionGroupProductVariantFragment,
    t: TFunction<'app'>,
    freeOfCharge: boolean,
    quantity?: number
): Addon => ({
    id: productVariant.uuid,
    name: formatAddonNameFromAdditionGroupProductVariant(productVariant),
    price: freeOfCharge ? t('freeOfCharge') : formatFreeOfChargeAblePrice(productVariant.price, t),
    soldOut: productVariant.soldOut,
    quantity,
})

const transformAdditionToAddonGroup = (
    additionGroup: AdditionGroupFragment,
    t: TFunction<'app'>,
    defaults?: AddonDefaults
): AddonGroup => ({
    id: additionGroup.uuid,
    type: 'product',
    name: additionGroup.name,
    minQuantity: additionGroup.minQuantity ?? 0,
    maxQuantity: additionGroup.maxQuantity ?? null,
    withRepeatableItems: additionGroup.withRepeatableItems,
    addons: additionGroup.productVariants.map((product) =>
        transformAdditionGroupProductVariantToAddon(
            product,
            t,
            additionGroup.freeOfCharge,
            defaults?.[product.uuid]
        )
    ),
})

const transformToppingToAddon = (
    topping: ToppingFragment,
    t: TFunction<'app'>,
    freeOfCharge: boolean,
    quantity?: number
): Addon => ({
    id: topping.uuid,
    name: topping.name,
    price: freeOfCharge ? t('freeOfCharge') : formatFreeOfChargeAblePrice(topping.price, t),
    soldOut: topping.soldOut,
    quantity,
})

const transformToppingToAddonGroup = (
    toppingGroup: ToppingGroupFragment,
    t: TFunction<'app'>,
    defaults?: AddonDefaults
): AddonGroup => ({
    id: toppingGroup.uuid,
    type: 'topping',
    name: toppingGroup.name,
    minQuantity: toppingGroup.minQuantity ?? 0,
    maxQuantity: toppingGroup.maxQuantity ?? null,
    withRepeatableItems: toppingGroup.withRepeatableItems,
    addons: toppingGroup.toppings.map((topping) =>
        transformToppingToAddon(topping, t, toppingGroup.freeOfCharge, defaults?.[topping.uuid])
    ),
})

const transformRemovableRecipeIngredient = (
    removableRecipeIngredient: RemovableRecipeIngredientFragment,
    removed?: boolean
): RemovableIngredient => ({
    id: removableRecipeIngredient.uuid,
    name: removableRecipeIngredient.name,
    removed,
})

const transformProductVariant = (
    productVariant: ProductVariantFragment,
    t: TFunction<'app'>,
    defaults?: ProductVariantDefaults
): ProductVariant => ({
    id: productVariant.uuid,
    name: productVariant.name ?? undefined,
    price: formatFreeOfChargeAblePrice(productVariant.price, t),
    soldOut: productVariant.soldOut,
    addonGroups: [
        ...productVariant.toppingGroups.map((group) =>
            transformToppingToAddonGroup(group, t, defaults?.toppings[group.uuid])
        ),
        ...productVariant.additionGroups.map((group) =>
            transformAdditionToAddonGroup(group, t, defaults?.additionProducts[group.uuid])
        ),
    ],
    removableIngredients: productVariant.removableRecipeIngredients.map((ingredient) =>
        transformRemovableRecipeIngredient(
            ingredient,
            defaults?.removableIngredients[ingredient.uuid]
        )
    ),
})

export const transformProductToConfigurable = (
    product: ConfigurableProductFragment,
    t: TFunction<'app'>,
    cartItem?: CartItemFragment
): ConfigurableProduct => {
    const defaults = cartItem && getProductVariantDefaults(cartItem)

    return {
        name: product.name,
        productVariants: product.productVariants.map((product) =>
            transformProductVariant(product, t, defaults)
        ),
        defaultProductVariantId: cartItem?.productVariant.uuid,
    }
}
